import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import useGetAdminOrganizationStripeBillingByMonth from '@/hooks/admin/organizations/useGetAdminOrganizationStripeBillingByMonth';
import PeriodPicker from '@/pages/admin/platformCosts/PeriodPicker';
import useSelectedCostMonth from '@/pages/admin/platformCosts/helpers/useSelectedCostMonth';
import SubscriptionBadge from '../SubscriptionBadge';
import normalizeSubscriptionStatus from '../helpers/normalizeSubscriptionStatus';
import formatMonthLabel from './helpers/formatMonthLabel';
import getMockOrganizationDetail from './helpers/getMockOrganizationDetail';
import getOrganizationDetailTabIcon from './helpers/getOrganizationDetailTabIcon';
import getOrganizationDetailTabPath from './helpers/getOrganizationDetailTabPath';
import getOrganizationDetailTabs from './helpers/getOrganizationDetailTabs';
import resolveOrganizationDetailTab from './helpers/resolveOrganizationDetailTab';
import type { OrganizationDetailOutletContext, OrganizationDetailTab } from './types';
import {
    ContentScroll,
    DetailContainer,
    TabButton,
    TabList,
    TopBar,
    TopBarLeft,
    TopBarSubtitle,
    TopBarTitle,
} from './styled';

function OrganizationDetail() {
    const { organizationId = '' } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { month, setMonth } = useSelectedCostMonth();
    const { billing, isLoadingBilling, error, refetch } =
        useGetAdminOrganizationStripeBillingByMonth({
            organizationId,
            month,
        });

    const mockDetail = useMemo(
        () => getMockOrganizationDetail({ organizationId }),
        [organizationId]
    );

    const monthLabel = formatMonthLabel({ month });
    const activeTab = resolveOrganizationDetailTab({ pathname: location.pathname });
    const subscriptionStatus = billing
        ? normalizeSubscriptionStatus({ status: billing.subscription.status })
        : null;

    const outletContext: OrganizationDetailOutletContext = {
        mockDetail,
        month,
        monthLabel,
        stripeBilling: {
            billing,
            isLoadingBilling,
            error,
            refetch,
        },
    };

    const isServicesTab = activeTab === 'services';
    const tabItems = getOrganizationDetailTabs();

    const handleNavigateToTab = ({ tab }: { tab: OrganizationDetailTab }) => {
        navigate(
            getOrganizationDetailTabPath({
                organizationId,
                tab,
                search: location.search,
            })
        );
    };

    return (
        <DetailContainer>
            <TopBar>
                <TopBarLeft>
                    <TopBarTitle>Created {mockDetail.createdAtLabel}</TopBarTitle>
                    <TopBarSubtitle>{mockDetail.ageLabel} old</TopBarSubtitle>
                </TopBarLeft>
                <PeriodPicker month={month} onMonthChange={setMonth} />
            </TopBar>

            <TabList>
                {tabItems.map(item => {
                    const isActive = item.tab === activeTab;
                    const TabIcon = getOrganizationDetailTabIcon({ tab: item.tab });

                    return (
                        <TabButton
                            key={item.tab}
                            type="button"
                            $isActive={isActive}
                            onClick={() => handleNavigateToTab({ tab: item.tab })}
                        >
                            <TabIcon aria-hidden="true" />
                            {item.label}
                            {item.tab === 'subscription' && subscriptionStatus ? (
                                <SubscriptionBadge status={subscriptionStatus} />
                            ) : null}
                        </TabButton>
                    );
                })}
            </TabList>

            <ContentScroll $layout={isServicesTab ? 'services' : 'default'}>
                <Outlet context={outletContext} />
            </ContentScroll>
        </DetailContainer>
    );
}

export default OrganizationDetail;
